import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { CurrencyCode } from "@domain/currency/currency";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useIsMobile } from "@utils/hooks";
import { Primary } from "@ui/components/containers/primary";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { createToast } from "@ui/toasts/utils";
import { ChangeEmailModal } from "./ChangeEmailModal";

import { useHistory } from "@core/routing/useHistory";
import { config } from "@core/config/config";
import { Path } from "@core/routing/path";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { ChangeNumberModal } from "./ChangeNumberModal";

interface LoginSettingsProps extends React.HTMLAttributes<HTMLDivElement> {
	onCurrencyChange?: (currency: CurrencyCode) => Promise<CurrentProfile>;
}

export const LoginSettings: React.FC<LoginSettingsProps> = ({ onCurrencyChange, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const { currentProfileService } = useServices();
	const askConfirmation = useConfirmation(I18n.t("settings.delete.account.label"), I18n.t("delete"), "");
	const { createModal } = useModalTools();
	const isMobile = useIsMobile();
	const styles = {
		customtd: {
			paddingTop: isMobile ? "0" : "35px",
		},

		tdchangemodifyemail: {
			margin: 0,
		},
	};
	// console.log("PHONE:",currentProfile?.phoneNumberAuth)

	const history = useHistory();

	return (
		<Container {...htmlProps}>
			<>
				{config.features.login.phone && (
					<tbody>
						<tr>
							<td>
								<Primary>
									<b>{I18n.t("settings.login.phonenumber.label")}</b>
								</Primary>
							</td>
							<td style={styles.customtd}>
								<Primary>
									<ViewText>
										<MaxWidth>{currentProfile?.phoneNumberAuth ?? I18n.t("register.phonenumber.placeholder")}</MaxWidth>
									</ViewText>
								</Primary>
							</td>
						</tr>
						<tr>
							{!isMobile ? <td></td> : null}
							<td style={styles.tdchangemodifyemail}>
								<Primary>
									<ModifyEmailText
										onClick={() => {
											if (currentProfile?.phoneNumberAuth) {
												createModal((close) => <ChangeNumberModal close={close} />);
											} else {
												history.push(Path.LinkNumberAction);
											}
										}}
									>
										{currentProfile?.phoneNumberAuth ? (
											<b>{I18n.t("settings.login.phonenumber.update")}</b>
										) : (
											<b>{I18n.t("settings.login.phonenumber.add")}</b>
										)}
									</ModifyEmailText>
								</Primary>
							</td>
						</tr>
					</tbody>
				)}
				{currentProfile?.email ? (
					<tbody>
						<tr>
							<td style={styles.customtd}>
								<Primary>
									<b>{I18n.t("settings.system.content.email.title")}</b>
								</Primary>
							</td>

							<td style={styles.customtd}>
								<Primary>
									<ViewText>
										<MaxWidth>{currentProfile?.email}</MaxWidth>
									</ViewText>
								</Primary>
							</td>
						</tr>
						<tr>
							{!isMobile ? <td></td> : null}
							<td style={styles.tdchangemodifyemail}>
								<Primary>
									<ModifyEmailText onClick={() => createModal((close) => <ChangeEmailModal close={close} />)}>
										<b>{I18n.t("settings.change.email.link.title")}</b>
									</ModifyEmailText>
								</Primary>
							</td>
						</tr>
						<tr>
							{!isMobile ? <td></td> : null}
							<td style={styles.tdchangemodifyemail}>
								<Primary>
									<LinkDeleteAccount
										onClick={() =>
											askConfirmation(async () => {
												try {
													await currentProfileService.delete();

													createToast(I18n.t("success"), I18n.t("settings.delete.account.success"), "success");
												} catch {
													createToast(I18n.t("error"), I18n.t("auth.error.default"), "error");
												}
											})
										}
									>
										<b>{I18n.t("settings.delete.account.link.title")}</b>
									</LinkDeleteAccount>
								</Primary>
							</td>
						</tr>
					</tbody>
				) : null}
			</>
		</Container>
	);
};
const Container = styled.table`
	width: 100%;
	@media (max-width: 900px) {
		table-layout: fixed;
		td {
			display: block;
			margin: 10px auto 10px;
			padding-top: 0;
		}
	}
`;

const ViewText = styled.div`
	background-color: #f3f4f6;
	padding: 13px 16px;
	border-radius: 13px;
`;
const MaxWidth = styled.div`
	@media (max-width: 900px) {
		display: block;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
`;

const ModifyEmailText = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
	${({ theme }) => theme.textStyles.light};
	color: ${({ theme, disabled }) => (disabled ? theme.colors.simplegray : theme.colors.vividBlue)};
	cursor: pointer;
	margin-top: 10px;
	@media (max-width: 900px) {
		margin-top: 0;
	}
`;

const LinkDeleteAccount = styled.div`
	${({ theme }) => theme.textStyles.light};
	color: ${({ theme }) => theme.colors.red};
	cursor: pointer;
	margin-top: 10px;
	@media (max-width: 900px) {
		margin-top: 0;
	}
`;
